import React, { useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export default function DockBar({
  playing,
  isFullscreen,
  autoHidden,
  manualHidden,
  title,
  album,
  hasVideo,
  isInstrumental,
  onTogglePlay,
  onBump,
  setAutoHidden,
  setManualHidden,
  isEmpty = false,
  onOpenEmpty = null
}) {
  const { t } = useTranslation();
  // DockBar 用于“唤起播放器”，因此不依赖是否已选歌/是否在播放
  const shouldRender = !isFullscreen && (isEmpty || manualHidden || (!playing && autoHidden));
  const displayTitle = title || (isEmpty ? t('player.noMusicSelected', '未选择音乐') : '');

  // 未选歌时点击/轻触 DockBar 同时播放的提示音
  const EMPTY_CLICK_AUDIO_URL = '/tuac/audio/tuac.opus';

  const playEmptyClickSound = useCallback(() => {
    try {
      const audio = new Audio(EMPTY_CLICK_AUDIO_URL);
      audio.volume = 0.6;
      audio.play().catch(() => {});
    } catch (_) {}
  }, []);

  // 手势识别相关状态
  const touchStartRef = useRef(null);
  const mouseStartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const containerRef = useRef(null);
  const MIN_SWIPE_DISTANCE = 30; // 最小滑动距离（px）

  // 处理触摸开始（原生事件，非 passive）
  const handleTouchStartNative = useCallback((e) => {
    if (e.touches.length === 1) {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: Date.now()
      };
      isDraggingRef.current = false;
    }
  }, []);

  // 处理触摸移动（原生事件，非 passive）
  const handleTouchMoveNative = useCallback((e) => {
    if (!touchStartRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    // 如果垂直移动距离大于水平移动距离，认为是垂直滑动
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
      isDraggingRef.current = true;
      if (e.cancelable) {
        e.preventDefault(); // 防止页面滚动（使用原生事件监听器，可以调用 preventDefault）
      }
    }
  }, []);

  // React 事件处理（用于兼容性，但不调用 preventDefault）
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: Date.now()
      };
      isDraggingRef.current = false;
    }
  }, []);

  // React 事件处理（用于兼容性，但不调用 preventDefault）
  const handleTouchMove = useCallback((e) => {
    if (!touchStartRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    // 如果垂直移动距离大于水平移动距离，认为是垂直滑动
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
      isDraggingRef.current = true;
    }
  }, []);

  // 处理触摸结束
  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) {
      touchStartRef.current = null;
      return;
    }

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const deltaTime = Date.now() - touchStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    const openNow = () => {
      // empty：播放提示音并由 onOpenEmpty 负责“先挂载(隐藏)->再滑入”的动画序列
      if (isEmpty) {
        playEmptyClickSound();
        try { onOpenEmpty?.(); } catch (_) {}
        return;
      }
      try { if (setAutoHidden) setAutoHidden(false); } catch (_) {}
      try { if (setManualHidden) setManualHidden(false); } catch (_) {}
    };

    // 判断是否为有效的滑动手势（即使没有标记为拖拽，也检查距离）
    if (distance > MIN_SWIPE_DISTANCE && deltaTime < 500 && Math.abs(deltaY) > Math.abs(deltaX)) {
      // 向上滑动：显示播放器面板
      if (deltaY < -MIN_SWIPE_DISTANCE) {
        // 只在事件可取消时才调用 preventDefault
        if (e.cancelable) {
          e.preventDefault();
        }
        e.stopPropagation();
        openNow();
      }
    } else {
      // 轻点（tap）：也展开播放器。
      // 说明：某些移动端浏览器在 touchmove 里 preventDefault 后不会触发 click，
      // 因此这里补一层 tap 展开，确保“未选择音乐”状态条也可唤起播放器。
      if (deltaTime < 450 && distance < 12) {
        openNow();
      }
    }

    touchStartRef.current = null;
    isDraggingRef.current = false;
  }, [setAutoHidden, setManualHidden, isEmpty, onOpenEmpty, playEmptyClickSound]);

  // 处理鼠标按下
  const handleMouseDown = useCallback((e) => {
    if (e.button === 0) { // 左键
      mouseStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        time: Date.now()
      };
      isDraggingRef.current = false;
    }
  }, []);

  // 处理鼠标移动
  const handleMouseMove = useCallback((e) => {
    if (!mouseStartRef.current) return;
    const deltaX = e.clientX - mouseStartRef.current.x;
    const deltaY = e.clientY - mouseStartRef.current.y;
    
    // 如果垂直移动距离大于水平移动距离，认为是垂直拖拽
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
      isDraggingRef.current = true;
    }
  }, []);

  // 处理鼠标抬起
  const handleMouseUp = useCallback((e) => {
    if (!mouseStartRef.current || !isDraggingRef.current) {
      mouseStartRef.current = null;
      return;
    }

    const deltaX = e.clientX - mouseStartRef.current.x;
    const deltaY = e.clientY - mouseStartRef.current.y;
    const deltaTime = Date.now() - mouseStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // 判断是否为有效的拖拽手势
    if (distance > MIN_SWIPE_DISTANCE && deltaTime < 500) {
      // 向上拖拽：显示播放器面板
      if (deltaY < -MIN_SWIPE_DISTANCE && Math.abs(deltaY) > Math.abs(deltaX)) {
        if (isEmpty) {
          playEmptyClickSound();
          try { onOpenEmpty?.(); } catch (_) {}
        } else {
          if (setAutoHidden) setAutoHidden(false);
          if (setManualHidden) setManualHidden(false);
        }
      }
    }

    mouseStartRef.current = null;
    isDraggingRef.current = false;
  }, [setAutoHidden, setManualHidden, isEmpty, onOpenEmpty, playEmptyClickSound]);

  // 处理鼠标离开（防止鼠标移出窗口时状态未重置）
  const handleMouseLeave = useCallback(() => {
    mouseStartRef.current = null;
    isDraggingRef.current = false;
  }, []);

  // 使用原生事件监听器来处理触摸事件（非 passive，可以调用 preventDefault）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStartNative, { passive: false });
    container.addEventListener('touchmove', handleTouchMoveNative, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleTouchStartNative);
      container.removeEventListener('touchmove', handleTouchMoveNative);
    };
  }, [handleTouchStartNative, handleTouchMoveNative]);

  if (typeof document === 'undefined') return null;
  if (!shouldRender) return null;

  return createPortal(
    <div
      ref={containerRef}
      className={[
        // empty 模式也使用 DockBar 的渐变条样式，避免看起来像旧的 EmptyPlayerBar
        'px-2 flex items-center',
        'border-t border-white/10'
      ].filter(Boolean).join(' ')}
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        // 高度约为原来的 1/3（64px -> ~22px）
        height: 'calc(22px + env(safe-area-inset-bottom))',
        boxSizing: 'border-box',
        paddingBottom: 'env(safe-area-inset-bottom)',
        backgroundImage: 'linear-gradient(180deg, rgba(16,185,129,0.55) 0%, rgba(5,150,105,0.85) 100%)',
        // 防止在状态条上滑动时触发页面滚动
        touchAction: 'none',
        overscrollBehavior: 'contain'
      }}
      onClick={(e) => {
        e.stopPropagation();
        // 点击 DockBar：
        // - empty：播放提示音并由 onOpenEmpty 负责“先挂载(隐藏)->再滑入”的动画序列
        // - 非 empty：直接展开播放器
        if (isEmpty) {
          playEmptyClickSound();
          try { onOpenEmpty?.(); } catch (_) {}
          return;
        }
        try { if (setAutoHidden) setAutoHidden(false); } catch (_) {}
        try { if (setManualHidden) setManualHidden(false); } catch (_) {}
      }}
      onMouseMove={(e) => {
        onBump?.();
        handleMouseMove(e);
      }}
      onMouseDown={(e) => {
        onBump?.();
        handleMouseDown(e);
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      data-no-toggle
    >
      <div className="w-full flex items-center gap-2">
        <div className="flex items-center gap-1 text-[10px] text-white/85 flex-shrink-0">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: playing ? '#ef4444' : '#9ca3af' }}
          />
          <span>{playing ? t('player.nowPlaying', '正在播放') : t('player.stopped', '停止播放')}</span>
        </div>
        <div className="min-w-0 flex-1 text-center overflow-x-auto overflow-y-hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="text-[12px] leading-none font-semibold text-white inline-flex items-center justify-center gap-1 min-w-max">
            {!isEmpty && hasVideo && (
              <img
                src="/icons/mtv-s.png"
                alt="MTV"
                className="w-4 h-4 flex-shrink-0 object-contain"
                title={t('player.hasVideo') || '视频'}
              />
            )}
            {!isEmpty && !hasVideo && isInstrumental && (
              <img
                src="/icons/mcd-s.png"
                alt={t('musicList.categoryInstrumental', '纯音乐')}
                className="w-4 h-4 flex-shrink-0 object-contain"
                title={t('musicList.categoryInstrumental', '纯音乐')}
              />
            )}
            <span className="whitespace-nowrap">{displayTitle}</span>
            <span className="text-white/60 font-normal whitespace-nowrap">
              {!isEmpty && album ? ` · ${album}` : ''}
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

